import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useGroupOrderingAvailability from "../../hooks/useGroupOrderingAvailability";
import { customerIdOf } from "../../hooks/useAnalyticsUser";
import { goToSignIn, pathOfLocation } from "../../utils/signInReturn";
import GroupBasketCreateDialog from "./GroupBasketCreateDialog";
import GroupBasketResumeCards from "./GroupBasketResumeCards";
import { FONT, SOCIAL, SOCIAL_SOFT, SOCIAL_LINE, MUTED, TEXT } from "./groupBasketStyles";

/*
 * "Order together" on a restaurant storefront, with the shopper's own basket
 * at this restaurant (if one is under way) above it. The action is shown only
 * when the server
 * says group ordering is on for restaurants in this market (it ships off).
 * Signed out, it goes through sign in and comes back here. The restaurant
 * must be open to start a basket, so the action waits while it is closed.
 */
const OrderTogetherEntry = ({ vendorId, outletId, restaurantName, orderingOpen }) => {
  const history = useHistory();
  const location = useLocation();
  const signedIn = useSelector((state) => Boolean(customerIdOf(state.user?.currentUser)));
  const { open, availability } = useGroupOrderingAvailability("restaurant");
  const [creating, setCreating] = useState(false);

  // A basket already under way here comes first, whatever the switch says.
  const resume = <GroupBasketResumeCards vendorId={vendorId} />;
  if (!open || !vendorId) return resume;

  const start = () => {
    if (!signedIn) {
      goToSignIn(history, pathOfLocation(location));
      return;
    }
    setCreating(true);
  };

  return (
    <>
      {resume}
      <Entry type="button" onClick={start} disabled={!orderingOpen}>
        <span className="icon" aria-hidden="true">
          <GroupsOutlinedIcon />
        </span>
        <span className="text">
          <strong>Order together</strong>
          <span>
            {orderingOpen
              ? "Share a link. Friends add their items to one order and one delivery."
              : "Available while the restaurant is open."}
          </span>
        </span>
        <ChevronRightIcon className="chevron" aria-hidden="true" />
      </Entry>
      {creating && (
        <GroupBasketCreateDialog
          open={creating}
          onClose={() => setCreating(false)}
          vendorId={vendorId}
          outletId={outletId}
          restaurantName={restaurantName}
          windows={availability?.windows}
        />
      )}
    </>
  );
};

const Entry = styled.button`
  width: 100%;
  max-width: 1440px;
  margin: 12px auto 0;
  min-height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${SOCIAL_LINE};
  border-radius: 16px;
  background: ${SOCIAL_SOFT};
  color: ${SOCIAL};
  font-family: ${FONT};
  text-align: left;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(21, 35, 42, 0.14);
  transition: background-color 150ms ease;

  &:hover:not(:disabled) {
    background: #f9e1ed;
  }

  &:focus-visible {
    outline: 3px solid ${SOCIAL};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    background: #e4e9e6;
    color: ${TEXT};
    box-shadow: none;

    .icon {
      background: #ffffff;
      color: ${MUTED};
    }

    .text span {
      color: ${MUTED};
    }
  }

  .icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${SOCIAL};
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    font-size: 16px;
    font-weight: 800;
  }

  .text span {
    font-size: 13.5px;
    line-height: 1.35;
    color: #634657;
  }

  .chevron {
    flex-shrink: 0;
  }
`;

export default OrderTogetherEntry;
